<script>
import { useAlert } from 'dashboard/composables';
import WeeklyHoursEditor from '../components/WeeklyHoursEditor.vue';
import { FIELD_CLASS } from '../constants';
import { toHourRows } from '../helpers';
import { reportInvalidForm, scrollToFirstError } from '../formFeedback';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import {
  createSchedule,
  patchSchedule,
  setScheduleHours,
  setScheduleAgents,
} from '../../../../api/realEstate/schedules';

function sameIds(a, b) {
  if (a.length !== b.length) return false;
  const sortedA = a.slice().sort();
  const sortedB = b.slice().sort();
  return sortedA.every((id, index) => id === sortedB[index]);
}

function sameHours(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

// Create sends one POST with working_hours and agent_ids together. Edit
// only calls what changed — PATCH name, PUT working-hours, PUT agents —
// each its own request (docs/real-estate/be/10-api-reference.md §A8).
export default {
  name: 'ScheduleModal',
  components: { WeeklyHoursEditor },
  props: {
    project: {
      type: Object,
      required: true,
    },
    schedule: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      form: {
        name: this.schedule ? this.schedule.name : '',
      },
      workingHours: this.schedule
        ? toHourRows(this.schedule.working_hours)
        : [],
      selectedAgentIds: this.schedule
        ? this.schedule.agents.map(a => a.agent_id)
        : [],
      isSubmitting: false,
      submitted: false,
    };
  },
  computed: {
    isEdit() {
      return !!this.schedule;
    },
    fieldErrors() {
      return {
        name: !this.form.name.trim(),
      };
    },
    isFormValid() {
      return !Object.values(this.fieldErrors).some(Boolean);
    },
  },
  methods: {
    toggleAgent(agentId) {
      this.selectedAgentIds = this.selectedAgentIds.includes(agentId)
        ? this.selectedAgentIds.filter(id => id !== agentId)
        : [...this.selectedAgentIds, agentId];
    },
    // The project has no agents yet — send the admin to assign one instead
    // of leaving them stuck on an empty checkbox list.
    scrollToAgentsSection() {
      this.$emit('close');
      this.$nextTick(() => {
        const el = document.getElementById('sec-agents');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    async handleSubmit() {
      this.submitted = true;
      const hoursValid = this.$refs.hoursEditor.isValid();
      if (!hoursValid) {
        useAlert(this.$t('REAL_ESTATE.ERRORS.INVALID_TIME_RANGE'));
      }
      if (!this.isFormValid || !hoursValid) {
        // The hours error already has its own alert above.
        if (hoursValid) reportInvalidForm(this);
        else scrollToFirstError(this);
        return;
      }
      this.isSubmitting = true;
      try {
        if (this.isEdit) {
          await this.saveEdit();
          useAlert(this.$t('REAL_ESTATE.SCHEDULES.SUCCESS_UPDATED'));
        } else {
          await createSchedule(this.project.property_id, {
            name: this.form.name.trim(),
            working_hours: this.workingHours,
            agent_ids: this.selectedAgentIds,
          });
          useAlert(this.$t('REAL_ESTATE.SCHEDULES.SUCCESS_ADDED'));
        }
        this.$emit('saved');
      } catch (err) {
        useAlert(
          apiErrorMessage(
            this,
            err,
            this.isEdit
              ? 'REAL_ESTATE.SCHEDULES.ERROR_UPDATE'
              : 'REAL_ESTATE.SCHEDULES.ERROR_ADD'
          )
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
    async saveEdit() {
      const scheduleId = this.schedule.schedule_id;
      const name = this.form.name.trim();
      const originalHours = toHourRows(this.schedule.working_hours);
      const originalAgentIds = this.schedule.agents.map(a => a.agent_id);

      const calls = [];
      if (name !== this.schedule.name) {
        calls.push(patchSchedule(scheduleId, name));
      }
      if (!sameHours(this.workingHours, originalHours)) {
        calls.push(setScheduleHours(scheduleId, this.workingHours));
      }
      if (!sameIds(this.selectedAgentIds, originalAgentIds)) {
        calls.push(setScheduleAgents(scheduleId, this.selectedAgentIds));
      }
      await Promise.all(calls);
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-xl max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{
            isEdit
              ? $t('REAL_ESTATE.SCHEDULES.MODAL_EDIT_TITLE')
              : $t('REAL_ESTATE.SCHEDULES.MODAL_ADD_TITLE')
          }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4" novalidate @submit.prevent="handleSubmit">
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.SCHEDULES.FIELD_NAME') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.name"
            :placeholder="$t('REAL_ESTATE.SCHEDULES.FIELD_NAME_PLACEHOLDER')"
            type="text"
            class="reset-base mb-0"
            :class="[
              fieldClass,
              {
                '!border-red-500': submitted && fieldErrors.name,
              },
            ]"
            required
          />
          <p
            v-if="submitted && fieldErrors.name"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
          </p>
        </div>

        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.SCHEDULES.FIELD_HOURS') }}
          </label>
          <WeeklyHoursEditor ref="hoursEditor" v-model="workingHours" />
        </div>

        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.SCHEDULES.FIELD_AGENTS') }}
          </label>
          <p v-if="!project.agents.length" class="text-sm text-slate-400">
            {{ $t('REAL_ESTATE.SCHEDULES.NO_PROJECT_AGENTS') }}
            <button
              type="button"
              class="text-woot-500 hover:underline font-medium"
              @click="scrollToAgentsSection"
            >
              {{ $t('REAL_ESTATE.SCHEDULES.NO_PROJECT_AGENTS_LINK') }}
            </button>
          </p>
          <div v-else class="space-y-2">
            <label
              v-for="agent in project.agents"
              :key="agent.agent_id"
              class="flex items-center gap-2 p-2 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="selectedAgentIds.includes(agent.agent_id)"
                @change="toggleAgent(agent.agent_id)"
              />
              <span class="text-sm text-slate-900 dark:text-slate-100">
                {{ agent.name }}
              </span>
            </label>
          </div>
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.SCHEDULES.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.SCHEDULES.SAVE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
